import React, { useEffect, useMemo, useState } from "react";
import { getPGIAssessmentMappings } from "../../api-helpers/PgiApiHelper";
import { PGIAssessmentMapping } from "../../interfaces/PGIAssessmentMapping";
import PGISankey from "./PGISankey";

interface QuestionGroup {
  key: string;
  isScore: boolean;
  order: number;
  title: string;
  deleted: boolean;
  rows: PGIAssessmentMapping[];
}

const STAGE_LEGEND = [
  { label: "Answer / Score", color: "#90A4B4" },
  { label: "Problem", color: "#6E9CC4" },
  { label: "Goal", color: "#8E94D6" },
  { label: "Intervention", color: "#9DC74E" },
];

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;
const distinct = (rows: PGIAssessmentMapping[], pick: (r: PGIAssessmentMapping) => string) =>
  new Set(rows.map(pick)).size;

// Unique assessment names in first-seen order. The API returns rows ordered by the
// clinical-forms "order", so preserving that order matches the clinical forms ordering.
const orderedAssessments = (rows: PGIAssessmentMapping[]): string[] => {
  const seen = new Set<string>();
  const out: string[] = [];
  rows.forEach((r) => {
    if (!seen.has(r.assessment)) {
      seen.add(r.assessment);
      out.push(r.assessment);
    }
  });
  return out;
};

const PGIAssessmentMappings = () => {
  const [rows, setRows] = useState<PGIAssessmentMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [assessment, setAssessment] = useState<string>("");

  useEffect(() => {
    (async () => {
      try {
        const data = await getPGIAssessmentMappings();
        setRows(data);
        const first = orderedAssessments(data)[0] ?? "";
        setAssessment(first);
      } catch (e) {
        console.error("Failed to fetch PGI assessment mappings:", e);
        setError("Could not load assessment mappings.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const assessments = useMemo(() => orderedAssessments(rows), [rows]);

  // group rows into per-question (answer path) and a single "total score" group (score path);
  // deleted questions are always hidden
  const groupsFor = (name: string): QuestionGroup[] => {
    const scoped = rows.filter(
      (r) => r.assessment === name && !r.questionDeleted,
    );
    const byKey = new Map<string, QuestionGroup>();
    scoped.forEach((r) => {
      const isScore = r.triggerType === "score";
      const key = isScore ? "__score__" : "q:" + r.question;
      let g = byKey.get(key);
      if (!g) {
        g = {
          key,
          isScore,
          order: isScore ? -1 : r.qOrder ?? 9999,
          title: isScore ? "Total assessment score" : r.question,
          deleted: !!r.questionDeleted,
          rows: [],
        };
        byKey.set(key, g);
      }
      g.rows.push(r);
    });
    return Array.from(byKey.values()).sort((a, b) => a.order - b.order);
  };

  const groups = assessment ? groupsFor(assessment) : [];

  const total = useMemo(
    () =>
      rows.filter((r) => r.assessment === assessment && !r.questionDeleted),
    [rows, assessment],
  );

  return (
    <>
      <div className="px-4 py-3">
        {loading && <div className="text-muted">Loading…</div>}
        {error && <div className="text-danger">{error}</div>}

        {!loading && !error && (
          <>
            {/* controls */}
            <div className="d-flex flex-wrap align-items-center gap-3 mb-3">
              <div>
                <label className="form-label small text-muted mb-1 d-block">
                  Assessment
                </label>
                <select
                  className="form-select form-select-sm w-auto"
                  value={assessment}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setAssessment(e.target.value)
                  }
                >
                  {assessments.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>

              <div className="ms-auto d-flex gap-3 small text-muted">
                {STAGE_LEGEND.map((s) => (
                  <span key={s.label} className="d-inline-flex align-items-center">
                    <span
                      style={{
                        display: "inline-block",
                        width: 12,
                        height: 12,
                        borderRadius: 2,
                        background: s.color,
                        marginRight: 5,
                      }}
                    />
                    {s.label}
                  </span>
                ))}
              </div>
            </div>

            {/* summary line */}
            <div className="small text-muted mb-2">
              {plural(total.length, "rule")} across{" "}
              {plural(groups.length, groups[0]?.isScore ? "score group" : "question")}{" "}
              • {plural(distinct(total, (r) => r.problemName), "problem")} •{" "}
              {plural(distinct(total, (r) => r.goalName), "goal")} •{" "}
              {plural(distinct(total, (r) => r.interventionName), "intervention")} — hover any
              box to trace its path · click to lock it
            </div>

            {/* per-question sankeys */}
            {groups.map((g) => {
              const counts = {
                answers: distinct(g.rows, (r) => r.answer),
                problems: distinct(g.rows, (r) => r.problemName),
                goals: distinct(g.rows, (r) => r.goalName),
                interventions: distinct(g.rows, (r) => r.interventionName),
              };
              return (
                <div key={g.key} className="border-bottom py-2">
                  <div className="pt-2">
                    <div
                      className="text-uppercase fw-semibold"
                      style={{ fontSize: 11, letterSpacing: "0.05em", color: "#9ca3af" }}
                    >
                      {g.isScore ? "Score → PGIs" : `Question ${g.order}`}
                    </div>
                    <h2
                      className="h5 mb-1"
                      style={{
                        color: "#062740",
                        textDecoration: g.deleted ? "line-through" : "none",
                      }}
                    >
                      {g.title}
                      {g.deleted && (
                        <span
                          className="badge ms-2"
                          style={{ background: "#fee2e2", color: "#b91c1c" }}
                        >
                          deleted
                        </span>
                      )}
                    </h2>
                    <div className="small text-muted mb-1">
                      {plural(g.rows.length, "rule")} •{" "}
                      {plural(counts.answers, g.isScore ? "score band" : "answer")} •{" "}
                      {plural(counts.problems, "problem")} •{" "}
                      {plural(counts.goals, "goal")} •{" "}
                      {plural(counts.interventions, "intervention")}
                    </div>
                  </div>
                  <PGISankey rows={g.rows} />
                </div>
              );
            })}

            {groups.length === 0 && (
              <div className="text-muted">No PGI mappings for this assessment.</div>
            )}
          </>
        )}
      </div>
    </>
  );
};

export default PGIAssessmentMappings;
